import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

if (!BACKEND_URL) {
  // Fail loudly at boot rather than firing requests at "undefined/api".
  throw new Error(
    "REACT_APP_BACKEND_URL is not set. Point it at the deployed Worker, " +
      "e.g. https://lumenflow-api.<subdomain>.workers.dev",
  );
}

export const API = `${BACKEND_URL.replace(/\/$/, "")}/api`;

const TOKEN_KEY = "finflo_session";

export const getToken = () => {
  try {
    return window.localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
};

export const setToken = (token) => {
  try {
    if (token) window.localStorage.setItem(TOKEN_KEY, token);
    else window.localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* storage unavailable (private mode) — the session stays in memory only */
  }
};

export const clearToken = () => setToken(null);

const api = axios.create({
  baseURL: API,
  withCredentials: true,
  timeout: 10000,
});

export const isRetryableReadError = (error) => {
  if (axios.isCancel(error)) return false;
  if ((error?.config?.method || "get").toLowerCase() !== "get") return false;
  const status = error?.response?.status;
  return !error?.response || [429, 503, 504].includes(status);
};

const retryDelay = (error, attempt) => {
  const retryAfter = Number(error?.response?.headers?.["retry-after"]);
  if (Number.isFinite(retryAfter) && retryAfter > 0) {
    return Math.min(retryAfter * 1000, 5000);
  }
  return 200 * (2 ** (attempt - 1)) + Math.floor(Math.random() * 100);
};

// The SPA and the API are on different Cloudflare origins, where Safari and
// Firefox block the third-party session cookie. The token is therefore sent
// explicitly; `withCredentials` stays on so a same-origin deployment still
// works off the cookie alone.
api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// A rejected token is unrecoverable — drop it so the app falls back to login
// instead of retrying with a credential the server has already refused.
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error?.response?.status === 401) clearToken();
    const config = error?.config;
    if (config && isRetryableReadError(error)) {
      config.__finfloRetryCount = (config.__finfloRetryCount || 0) + 1;
      if (config.__finfloRetryCount <= 2) {
        await new Promise((resolve) => {
          setTimeout(resolve, retryDelay(error, config.__finfloRetryCount));
        });
        return api(config);
      }
    }
    return Promise.reject(error);
  },
);

export const loginUrl = () => `${API}/auth/google/login`;

export default api;
